/**
 * @file ContactsList.tsx
 * @description Список строк раздела «Контакты» или «Соцсети» на «Настройках
 * сайта» (phase19.16 Р1; SB18; uiux-design-r1 §4): строки, порядок, «+
 * Добавить». Для контактов — кнопка на каждый ещё не добавленный вид, для
 * соцсетей — одна «+ Соцсеть» (вид выбирается в строке).
 */

import { SITE_CONTACT_KINDS, SITE_LIMITS, type SiteContactKind } from "@chaiberi/shared";
import { Plus } from "lucide-react";

import { Button } from "../../ui/button";
import { ContactRowEditor } from "./ContactRowEditor";
import { moveRow, newContactRow, type ContactRow } from "../../../lib/site-view/contacts-edit";

interface Props {
  rows: ContactRow[];
  kinds: readonly SiteContactKind[];
  /** Соцсети: вид выбирается в строке; контакты — по кнопке на вид. */
  social: boolean;
  /** Всего строк в обоих разделах (лимит один на список). */
  total: number;
  shopEmail: string | null;
  shopSettingsHref: string;
  onChange: (rows: ContactRow[]) => void;
}

export function ContactsList({ rows, kinds, social, total, shopEmail, shopSettingsHref, onChange }: Props) {
  const full = total >= SITE_LIMITS.contacts;
  const missing = kinds.filter((k) => !rows.some((r) => r.kind === k));
  const update = (key: string, next: ContactRow) => onChange(rows.map((r) => (r.key === key ? next : r)));

  return (
    <div className="flex flex-col gap-2">
      {rows.length === 0 ? (
        <p className="type-body text-on-surface-variant">
          {social ? "Соцсетей нет — значков в подвале не будет." : "Контактов нет — в подвале останутся сведения о продавце."}
        </p>
      ) : null}
      {rows.map((row, index) => (
        <ContactRowEditor
          key={row.key}
          row={row}
          kinds={social ? kinds : [row.kind]}
          shopEmail={shopEmail}
          shopSettingsHref={shopSettingsHref}
          first={index === 0}
          last={index === rows.length - 1}
          onChange={(next) => update(row.key, next)}
          onMove={(delta) => onChange(moveRow(rows, row.key, delta))}
          onRemove={() => onChange(rows.filter((r) => r.key !== row.key))}
        />
      ))}
      <div className="flex flex-wrap gap-2">
        {social ? (
          <Button variant="outline" disabled={full || kinds.length === 0} onClick={() => onChange([...rows, newContactRow(missing[0] ?? kinds[0]!)])}>
            <Plus aria-hidden />
            Соцсеть
          </Button>
        ) : (
          missing.map((kind) => (
            <Button key={kind} variant="outline" disabled={full} onClick={() => onChange([...rows, newContactRow(kind)])}>
              <Plus aria-hidden />
              {SITE_CONTACT_KINDS[kind].label}
            </Button>
          ))
        )}
      </div>
    </div>
  );
}
